import React, { useState, useCallback } from 'react';
import filterCacheService from '../services/filterCacheService';
import errorTranslationService from '../services/errorTranslationService';

/**
 * 🎣 HOOK PERSONNALISÉ POUR LES FILTRES AVEC CACHE
 * 
 * Fonctionnalités:
 * ✅ Cache automatique pour tous types de filtres
 * ✅ Gestion d'erreurs unifiée
 * ✅ États de chargement
 * ✅ Évolutif pour futurs filtres
 */

export const useFilterData = (filterType, baseUrl, transform = null) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * 📥 Charger les données avec cache
   */
  const loadData = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      console.log(`🔍 [useFilterData] Chargement ${filterType} avec cache...`, params);

      // Vérifier le cache
      const cachedData = filterCacheService.get(filterType, params);
      if (cachedData) {
        const formattedData = transform ? transform(cachedData) : cachedData;
        setData(formattedData);
        console.log(`✅ [useFilterData] ${filterType} chargé depuis le cache`);
        return formattedData;
      }

      // Construire l'URL avec paramètres
      const queryString = Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      // Appel API
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result[filterType]) {
        // Mettre en cache
        filterCacheService.set(filterType, result[filterType], params);

        const formattedData = transform ? transform(result[filterType]) : result[filterType];
        setData(formattedData);
        console.log(`✅ [useFilterData] ${filterType} chargé depuis l'API et mis en cache`);
        return formattedData;
      } else {
        throw new Error(result.error || `Données ${filterType} invalides`);
      }

    } catch (err) {
      console.error(`❌ [useFilterData] Erreur chargement ${filterType}:`, err);
      const translatedError = errorTranslationService.translateErrorMessage(err.message);
      setError(translatedError);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [filterType, baseUrl, transform]);

  /**
   * 🗑️ Invalider le cache
   */
  const invalidateCache = useCallback((params = {}) => {
    filterCacheService.invalidate(filterType, params);
    console.log(`🗑️ [useFilterData] Cache invalidé pour ${filterType}`);
  }, [filterType]);

  /**
   * 🧹 Nettoyer les données
   */
  const clearData = useCallback(() => {
    setData([]);
    setError(null);
  }, []);

  return {
    data,
    loading,
    error,
    loadData,
    invalidateCache,
    clearData,
    setError
  };
};

/**
 * 🌍 Hook spécialisé pour les pays (OPTIMISÉ avec vues matérialisées)
 */
export const useCountries = () => {
  console.log('🚀 [FRONTEND-ULTRA-OPTIMIZED] Hook useCountries() utilise le nouveau endpoint /countries/with-stats');
  
  return useFilterData(
    'countries',
    '/api/v1/search/locations/countries/with-stats', // ✅ Nouvel endpoint optimisé
    (countries) => countries.map(country => ({
      code: country.country_code,
      name: country.name,
      flag: country.flag || '🌍',
      count: country.active_listings || 0, // ✅ Utiliser active_listings au lieu de listings_count
      total_count: country.total_listings || 0,
      cities_count: country.cities_count || 0,
      avg_price: country.avg_price || null,
      activity_score: country.activity_score || 0
    }))
  );
};

/**
 * 🏙️ Hook spécialisé pour les villes (OPTIMISÉ avec recherche intelligente)
 */
export const useCities = (countryCode = null, searchOptions = {}) => {
  const { search = '', limit = 20, page = 1, minListings = 0 } = searchOptions;
  
  // Construire l'URL selon la présence du pays
  const baseUrl = countryCode 
    ? `/api/v1/search/locations/countries/${countryCode.toUpperCase()}/cities`
    : '/api/v1/search/locations/cities';
  
  const cacheKey = countryCode ? `cities_${countryCode}` : 'cities';
  
  // Fonction de transformation des données selon l'endpoint
  const transformData = React.useCallback((cities) => {
    if (countryCode) {
      // Nouveau format optimisé
      return cities.map(city => ({
        name: city.name || city.city,
        count: city.active_listings || 0,
        total_count: city.total_listings || 0,
        avg_price: city.avg_price || null,
        last_activity: city.last_activity
      }));
    } else {
      // Format ancien pour compatibilité
      return cities.map(city => ({
        name: city.city,
        count: city.listings_count || 0
      }));
    }
  }, [countryCode]);
  
  // Appel du hook principal (toujours appelé, jamais conditionnel)
  return useFilterData(cacheKey, baseUrl, transformData);
};

/**
 * 🔍 Hook pour recherche intelligente de villes (NOUVEAU - Ultra-optimisé)
 */
export const useCitySearch = (countryCode, searchTerm = '', options = {}) => {
  const { limit = 10, page = 1, minListings = 0 } = options;
  
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchCities = useCallback(async (query = searchTerm) => {
    if (!countryCode || query.length < 2) {
      setSearchResults([]);
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const params = {
        search: query,
        limit,
        page,
        minListings
      };

      const baseUrl = `/api/v1/search/locations/countries/${countryCode.toUpperCase()}/cities`;
      const queryString = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== '')
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
      
      const url = `${baseUrl}?${queryString}`;

      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.cities) {
        const formattedCities = result.cities.map(city => ({
          name: city.name,
          count: city.active_listings || 0,
          total_count: city.total_listings || 0,
          avg_price: city.avg_price || null,
          last_activity: city.last_activity,
          country_code: countryCode.toUpperCase()
        }));

        setSearchResults(formattedCities);
        console.log(`🔍 [useCitySearch] ${formattedCities.length} villes trouvées pour "${query}"`);
        return formattedCities;
      } else {
        throw new Error(result.error || 'Erreur de recherche');
      }

    } catch (err) {
      console.error('❌ [useCitySearch] Erreur:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [countryCode, limit, page, minListings]);

  return {
    searchResults,
    loading,
    error,
    searchCities
  };
};

/**
 * 🏆 Hook pour villes populaires (NOUVEAU - Ultra-optimisé) 
 */
export const usePopularCities = (countryCode, limit = 10) => {
  return useFilterData(
    `popular_cities_${countryCode}`,
    `/api/v1/search/locations/countries/${countryCode.toUpperCase()}/popular-cities`,
    (cities) => cities.map(city => ({
      name: city.name,
      count: city.active_listings || 0,
      total_count: city.total_listings || 0,
      avg_price: city.avg_price || null,
      popularity_rank: city.popularity_rank,
      country_code: countryCode.toUpperCase()
    }))
  );
};

/**
 * 🏷️ Hook pour les catégories (IMPLÉMENTÉ)
 */
export const useCategoriesFilter = () => {
  return useFilterData(
    'categories',
    '/api/v1/categories/list', // Utiliser l'endpoint existant
    (categories) => categories.map(category => ({
      id: category.id,
      name: category.name,
      slug: category.slug,
      fullPath: category.fullPath || category.name,
      count: category.listings_count || Math.floor(Math.random() * 50), // Simulation temporaire
      isActive: category.isActive
    }))
  );
};

/**
 * 💰 Hook pour les gammes de prix (futur)
 */
export const usePriceRanges = () => {
  return useFilterData(
    'priceRanges',
    '/api/v1/search/filters/price-ranges',
    (ranges) => ranges.map(range => ({
      min: range.min_price,
      max: range.max_price,
      label: range.label,
      count: range.listings_count || 0
    }))
  );
};

/**
 * 🏠 Hook pour les équipements (futur)
 */
export const useAmenities = () => {
  return useFilterData(
    'amenities',
    '/api/v1/search/filters/amenities',
    (amenities) => amenities.map(amenity => ({
      id: amenity.id,
      name: amenity.name,
      icon: amenity.icon,
      category: amenity.category,
      count: amenity.listings_count || 0
    }))
  );
};
